import React, { useState, useEffect } from 'react';
import { FiMoreVertical, FiAlertTriangle, FiVolumeX, FiSlash, FiCheck, FiShield } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import BlockUserModal from './BlockUserModal';
import MuteUserModal from './MuteUserModal';
import './UserActionMenu.css';

/**
 * UserActionMenu - PRD-020 Module 2: Block/Mute/Report functionality
 * Dropdown menu for user moderation actions
 */
export default function UserActionMenu({ userId, userName, onActionComplete }) {
    const { supabase, currentUser } = useAuth();
    const [showMenu, setShowMenu] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [showBlockModal, setShowBlockModal] = useState(false);
    const [showMuteModal, setShowMuteModal] = useState(false);
    const [reportReason, setReportReason] = useState('');
    const [reportDescription, setReportDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [isUserTrusted, setIsUserTrusted] = useState(false);
    const [checkingTrust, setCheckingTrust] = useState(true);

    const reportReasons = [
        'Spam or advertising',
        'Harassment or bullying',
        'Inappropriate content',
        'Scamming or fraud',
        'Impersonation',
        'Other'
    ];

    // Check if user is trusted for links
    useEffect(() => {
        if (!userId) return;

        const checkTrustStatus = async () => {
            setCheckingTrust(true);
            try {
                const { data, error } = await supabase.rpc('check_link_trust', {
                    p_trusted_user_id: userId
                });

                if (error) {
                    console.error('Error checking trust status:', error);
                    setIsUserTrusted(false);
                } else {
                    setIsUserTrusted(data === true);
                }
            } catch (err) {
                console.error('Error checking trust:', err);
                setIsUserTrusted(false);
            } finally {
                setCheckingTrust(false);
            }
        };

        checkTrustStatus();
    }, [userId, supabase]);

    const handleBlock = () => {
        console.log('UserActionMenu: Opening block modal for user:', userName);
        setShowBlockModal(true);
        setShowMenu(false);
    };

    const handleBlockConfirm = async () => {
        console.log('UserActionMenu: Confirming block for userId:', userId);
        setShowBlockModal(false);
        setError(null);

        try {
            const { data, error: blockError } = await supabase.rpc('block_user', {
                p_user_id: userId
            });

            if (blockError) throw blockError;

            if (!data.success) {
                throw new Error(data.message || 'Failed to block user');
            }

            setSuccess('User blocked successfully');
            setTimeout(() => {
                setSuccess(null);
                if (onActionComplete) onActionComplete('block');
            }, 1500);
        } catch (err) {
            console.error('Error blocking user:', err);
            setError(err.message || 'Failed to block user');
        }
    };

    const handleMute = () => {
        console.log('UserActionMenu: Opening mute modal for user:', userName);
        setShowMuteModal(true);
        setShowMenu(false);
    };

    const handleMuteConfirm = async () => {
        console.log('UserActionMenu: Confirming mute for userId:', userId);
        setShowMuteModal(false);
        setError(null);

        try {
            const { data, error: muteError } = await supabase.rpc('mute_user', {
                p_user_id: userId
            });

            if (muteError) throw muteError;

            if (!data.success) {
                throw new Error(data.message || 'Failed to mute user');
            }

            setSuccess('User muted successfully');
            setTimeout(() => {
                setSuccess(null);
                if (onActionComplete) onActionComplete('mute');
            }, 1500);
        } catch (err) {
            console.error('Error muting user:', err);
            setError(err.message || 'Failed to mute user');
        }
    };

    const handleToggleLinkTrust = async () => {
        setShowMenu(false);
        setError(null);

        try {
            if (isUserTrusted) {
                // Remove trust
                const { data, error: trustError } = await supabase.rpc('remove_link_trust', {
                    p_trusted_user_id: userId
                });

                if (trustError) throw trustError;

                if (data.success) {
                    setIsUserTrusted(false);
                    setSuccess('Links from this user are now blocked');
                    setTimeout(() => {
                        setSuccess(null);
                        if (onActionComplete) onActionComplete('untrust');
                    }, 1500);
                } else {
                    throw new Error(data.message || 'Failed to remove trust');
                }
            } else {
                // Add trust
                const { data, error: trustError } = await supabase.rpc('add_link_trust', {
                    p_trusted_user_id: userId
                });

                if (trustError) throw trustError;

                if (data.success) {
                    setIsUserTrusted(true);
                    setSuccess('You can now view links from this user');
                    setTimeout(() => {
                        setSuccess(null);
                        if (onActionComplete) onActionComplete('trust');
                    }, 1500);
                } else {
                    throw new Error(data.message || 'Failed to add trust');
                }
            }
        } catch (err) {
            console.error('Error toggling link trust:', err);
            setError(err.message || 'Failed to update link trust');
        }
    };

    const handleReportSubmit = async (e) => {
        e.preventDefault();

        if (!reportReason) {
            setError('Please select a reason');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const { data, error: reportError } = await supabase.rpc('report_user', {
                p_reported_user_id: userId,
                p_reason: reportReason,
                p_description: reportDescription.trim() || null
            });

            if (reportError) throw reportError;

            if (!data.success) {
                throw new Error(data.message || 'Failed to submit report');
            }

            setSuccess('Report submitted successfully. Our moderation team will review it shortly.');
            setTimeout(() => {
                setShowReportModal(false);
                setShowMenu(false);
                setReportReason('');
                setReportDescription('');
                setSuccess(null);
                if (onActionComplete) onActionComplete('report');
            }, 2000);
        } catch (err) {
            console.error('Error reporting user:', err);
            setError(err.message || 'Failed to submit report');
        } finally {
            setSubmitting(false);
        }
    };

    if (!currentUser || userId === currentUser.id) {
        return null; // Don't show menu for own profile
    }

    return (
        <div className="user-action-menu-container">
            <button
                className="btn-action-menu"
                onClick={() => setShowMenu(!showMenu)}
                title="User actions"
            >
                <FiMoreVertical />
            </button>

            {showMenu && (
                <>
                    <div
                        className="menu-overlay"
                        onClick={() => setShowMenu(false)}
                    />
                    <div className="action-menu-dropdown">
                        {success && (
                            <div className="menu-message success">
                                <FiCheck /> {success}
                            </div>
                        )}

                        {error && (
                            <div className="menu-message error">
                                <FiAlertTriangle /> {error}
                            </div>
                        )}


                        <button
                            className="menu-item"
                            onClick={handleToggleLinkTrust}
                            disabled={checkingTrust}
                        >
                            <FiShield className={`menu-icon ${isUserTrusted ? 'trust-active' : 'trust-inactive'}`} />
                            <span>
                                {checkingTrust ? 'Checking...' : isUserTrusted ? 'Block Links' : 'Allow Links'}
                            </span>
                        </button>

                        <div className="menu-divider" />
                        <button
                            className="menu-item"
                            onClick={() => { setShowReportModal(true); setShowMenu(false); }}
                        >
                            <FiAlertTriangle className="menu-icon report" />
                            <span>Report User</span>
                        </button>

                        <button
                            className="menu-item"
                            onClick={handleMute}
                        >
                            <FiVolumeX className="menu-icon mute" />
                            <span>Mute User</span>
                        </button>

                        <button
                            className="menu-item"
                            onClick={handleBlock}
                        >
                            <FiSlash className="menu-icon block" />
                            <span>Block User</span>
                        </button>
                    </div>
                </>
            )}

            {/* Block Modal */}
            <BlockUserModal
                isOpen={showBlockModal}
                userName={userName}
                userId={userId}
                onConfirm={handleBlockConfirm}
                onCancel={() => setShowBlockModal(false)}
            />

            {/* Mute Modal */}
            <MuteUserModal
                isOpen={showMuteModal}
                userName={userName}
                userId={userId}
                onConfirm={handleMuteConfirm}
                onCancel={() => setShowMuteModal(false)}
            />

            {/* Report Modal */}
            {showReportModal && (
                <div className="modal-overlay" onClick={() => setShowReportModal(false)}>
                    <div className="report-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="report-modal-header">
                            <h3>Report {userName}</h3>
                            <p>Help us understand what's happening</p>
                        </div>

                        <form onSubmit={handleReportSubmit}>
                            {error && (
                                <div className="report-error">
                                    <FiAlertTriangle /> {error}
                                </div>
                            )}

                            {success && (
                                <div className="report-success">
                                    <FiCheck /> {success}
                                </div>
                            )}

                            <div className="form-group">
                                <label>Reason *</label>
                                <select
                                    value={reportReason}
                                    onChange={(e) => setReportReason(e.target.value)}
                                    required
                                    disabled={submitting}
                                >
                                    <option value="">Select a reason...</option>
                                    {reportReasons.map(reason => (
                                        <option key={reason} value={reason}>
                                            {reason}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Additional Details (Optional)</label>
                                <textarea
                                    value={reportDescription}
                                    onChange={(e) => setReportDescription(e.target.value)}
                                    placeholder="Provide any additional context..."
                                    rows="4"
                                    maxLength="1000"
                                    disabled={submitting}
                                />
                                <small>{reportDescription.length}/1000 characters</small>
                            </div>

                            <div className="report-modal-actions">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowReportModal(false)}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={submitting || !reportReason}
                                >
                                    {submitting ? 'Submitting...' : 'Submit Report'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
